/**
 * A worker-backed, windowed row source for the cue table.
 *
 * This is the piece that makes "documents live in the worker" true in practice
 * rather than in principle. The table asks for the row at an index; if that
 * page is cached it comes back synchronously, and if it is not, the source
 * returns null, fetches the page it belongs to, and repaints when it lands.
 * Nothing on the main thread ever holds more than a few hundred cues.
 *
 * The numbers behind that, measured by CORE on a desktop: one
 * `structuredClone` of a 40,000-cue document costs 1,230 ms coalesced and
 * 2,532 ms with word runs; a 60-cue window costs 1.1 ms. A phone is four to
 * eight times worse. There is no version of "just send the document" that
 * fits in a frame.
 *
 * Pages are aligned and cached by page index, so scrolling back over ground
 * already covered costs nothing, and a slider drag that crosses a boundary
 * costs one request rather than one per row.
 */

import type { DocumentId } from '../../../../../contracts/cue-model.ts';
import type { CuePreviewRow, PreviewPayload } from '../../../../../src/core/protocol.ts';
import { T } from '../../../../../src/core/time/ticks.ts';
import { formatTimestamp, timeFormat } from '../../../../../src/core/time/timestamp.ts';
import type { CueRow, CueTableSource } from './cue-table.ts';

/**
 * Rows per request. Large enough that a fling crosses few boundaries, small
 * enough that one page is nowhere near the clone cost that matters: 256 cues
 * is roughly 4 ms against the 1,230 ms a whole document would take.
 */
const PAGE = 256;
/** Pages kept. Four covers a screen plus a fling in either direction. */
const MAX_PAGES = 8;

/** The cue-model's own timestamp helper, in the VTT dialect: stable column width. */
const DISPLAY_FORMAT = timeFormat('vtt');

function rowOf(row: CuePreviewRow, fallbackIndex: number): CueRow {
  const start = formatTimestamp(T.ticks(row.startTicks), DISPLAY_FORMAT);
  const end = formatTimestamp(T.ticks(row.endTicks), DISPLAY_FORMAT);
  return {
    id: row.id,
    n: row.index ?? fallbackIndex + 1,
    time: `${start} → ${end}`,
    start,
    text: row.text,
  };
}

export interface CueWindowOptions {
  /** Issue one `preview` request. Injected so this module needs no worker to test. */
  readonly fetchPage: (from: number, count: number) => Promise<PreviewPayload>;
  /** Called when a page lands, so the table can repaint. */
  readonly onPage: () => void;
}

export interface CueWindow extends CueTableSource {
  /** Point at a document and drop everything cached for the previous one. */
  reset: (docId: DocumentId | null, total: number) => void;
  /** Forget the cache but keep the document — after an op changed the cues. */
  invalidate: (total?: number) => void;
  readonly total: () => number;
}

export function createCueWindow(options: CueWindowOptions): CueWindow {
  const pages = new Map<number, readonly CueRow[]>();
  const inFlight = new Set<number>();
  let docId: DocumentId | null = null;
  let total = 0;

  function evict(): void {
    // Oldest-first: a Map iterates in insertion order, and the pages a user is
    // looking at were inserted most recently.
    while (pages.size > MAX_PAGES) {
      const oldest = pages.keys().next();
      if (oldest.done) return;
      pages.delete(oldest.value);
    }
  }

  function request(page: number): void {
    if (docId === null || inFlight.has(page) || pages.has(page)) return;
    inFlight.add(page);
    options
      .fetchPage(page * PAGE, PAGE)
      .then((payload) => {
        // A response for a document we have moved on from is not an error, it
        // is a race, and dropping it is the whole handling.
        if (docId === null || payload.docId !== docId) return;
        total = payload.total;
        pages.set(
          page,
          payload.rows.map((row, i) => rowOf(row, payload.from + i)),
        );
        evict();
        options.onPage();
      })
      .catch(() => {
        // A failed page is not a failed screen. The row stays blank and the
        // next scroll past it tries again.
      })
      .finally(() => {
        inFlight.delete(page);
      });
  }

  return {
    count: () => total,
    total: () => total,
    row(index) {
      if (index < 0 || index >= total) return null;
      const page = Math.floor(index / PAGE);
      const cached = pages.get(page);
      if (cached) {
        const row = cached[index - page * PAGE];
        if (row) return row;
      }
      request(page);
      // Prefetch the neighbour a fling is about to reach.
      const offset = index - page * PAGE;
      if (offset > PAGE - 32) request(page + 1);
      else if (offset < 32 && page > 0) request(page - 1);
      return null;
    },
    reset(nextDocId, nextTotal) {
      docId = nextDocId;
      total = nextTotal;
      pages.clear();
      inFlight.clear();
    },
    invalidate(nextTotal) {
      if (nextTotal !== undefined) total = nextTotal;
      pages.clear();
      inFlight.clear();
    },
  };
}
